import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { MailIcon, PhoneIcon, FacebookIcon, DownloadIcon } from 'lucide-react';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const contacts = [{
  icon: MailIcon,
  label: 'Email',
  value: 'rmaddoxr@gmail.com',
  href: 'mailto:rmaddoxr@gmail.com'
}, {
  icon: PhoneIcon,
  label: 'Phone',
  value: '(817) 659-7124',
  href: 'tel:+18176597124'
}, {
  icon: FacebookIcon,
  label: 'Facebook',
  value: 'Robert Maddox',
  href: 'https://facebook.com'
}];
export function ContactSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="contact" ref={ref} className="relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #0a1628 0%, #0d1f4e 40%, #1a3a8f 80%, #1e4db7 100%)',
    paddingTop: '80px',
    paddingBottom: '0'
  }} data-id="element-54">
      {/* Dot grid overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '28px 28px'
    }} data-id="element-55" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 pb-16" data-id="element-56">
        <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="flex flex-col items-center text-center" data-id="element-57">
          {/* Heading */}
          <motion.h2 variants={itemVariants} className="text-white font-bold" style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 700,
          letterSpacing: '0.02em',
          marginBottom: '16px'
        }} data-id="element-58">
            Let's Connect
          </motion.h2>

          {/* Subtext */}
          <motion.p variants={itemVariants} className="max-w-xl" style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '15px',
          color: 'rgba(255,255,255,0.65)',
          lineHeight: '1.75',
          marginBottom: '48px'
        }} data-id="element-59">
            If you are seeking a strategic leader to drive transformation,
            improve execution, and deliver measurable results, I'd welcome the
            opportunity to connect.
          </motion.p>

          {/* Contact cards */}
          <motion.div variants={containerVariants} className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-60">
            {contacts.map(({
            icon: Icon,
            label,
            value,
            href
          }) => <motion.a key={label} variants={itemVariants} href={href} className="flex flex-col items-center gap-3 p-6 rounded-xl no-underline transition-all duration-200" style={{
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            textDecoration: 'none'
          }} onMouseEnter={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.backgroundColor = 'rgba(255,255,255,0.13)';
            el.style.borderColor = 'rgba(255,255,255,0.25)';
          }} onMouseLeave={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.backgroundColor = 'rgba(255,255,255,0.08)';
            el.style.borderColor = 'rgba(255,255,255,0.12)';
          }} data-id="element-61">
                {/* Icon circle */}
                <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{
              backgroundColor: 'rgba(255,255,255,0.12)'
            }} data-id="element-62">
                  <Icon size={20} style={{
                color: 'rgba(255,255,255,0.85)'
              }} data-id="element-63" />
                </div>
                <p className="font-bold" style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: 'white',
              fontWeight: 700
            }} data-id="element-64">
                  {label}
                </p>
                <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.6)'
            }} data-id="element-65">
                  {value}
                </p>
              </motion.a>)}
          </motion.div>

          {/* View Resume button */}
          <motion.a variants={itemVariants} href="https://cdn.magicpatterns.com/uploads/fQs35j5VZ81uHZtVubjArZ/Resume.Maddox.png" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-semibold transition-all duration-200" style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          backgroundColor: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.25)',
          color: 'white',
          textDecoration: 'none'
        }} onMouseEnter={e => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.backgroundColor = 'rgba(255,255,255,0.22)';
        }} onMouseLeave={e => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.backgroundColor = 'rgba(255,255,255,0.15)';
        }} data-id="element-66">
            <DownloadIcon size={16} data-id="element-67" />
            View Resume
          </motion.a>
        </motion.div>
      </div>

      {/* Footer bar */}
      <div className="relative z-10 w-full py-5 text-center" style={{
      borderTop: '1px solid rgba(255,255,255,0.1)'
    }} data-id="element-68">
        <p style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.08em'
      }} data-id="element-69">
          © 2026 ROBERT MADDOX. All rights reserved.
        </p>
      </div>
    </section>;
}